import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from "@mui/material";
import useSortableTable from "../../hooks/useSortableTable";
import { useNavigate } from "react-router-dom";
import RowActionsMenu from "../UI/RowActionsMenu";
import EmptyState from "../UI/EmptyState";
import SearchBar from "../UI/SearchBar";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditUserDialog from "./EditUserDialog";
import DeleteUserDialog from "./DeleteUserDialog";
import transparentLogo from "../../transparent-logo.png";

const UserTable = (props) => {
  const navigate = useNavigate();
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [search, setSearch] = useState("");

  const rows = props.users.map(
    ({ id, discriminator, discordId, username, email, icon, originId }) => ({
      id,
      discriminator,
      discordId,
      username,
      email,
      icon,
      originId,
    })
  );

  const { sorted: sortedRows, sortKey, sortDir, handleSort } = useSortableTable(rows, "username");

  if (rows.length === 0) {
    return <EmptyState icon={<PersonOutlineIcon />} message="No users found." />;
  }

  const q = search.toLowerCase();
  const sorted = sortedRows.filter(
    (row) =>
      (row.username || "").toLowerCase().includes(q) ||
      (row.email || "").toLowerCase().includes(q) ||
      (row.discordId || "").toLowerCase().includes(q)
  );

  const noResults = sorted.length === 0;

  return (
    <>
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search by username, email or Discord ID..."
      />

      {noResults ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          No users match &ldquo;{search}&rdquo;.
        </Typography>
      ) : (
        <TableContainer
          className="tableContainer"
          data-testid="userTable"
          component={Paper}
          sx={{ overflowX: "auto" }}
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                {props.showActions && <TableCell sx={{ width: 48, p: 0.5 }} />}
                <TableCell>Discriminator</TableCell>
                <TableCell>Discord ID</TableCell>
                <TableCell sortDirection={sortKey === "username" ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === "username"}
                    direction={sortKey === "username" ? sortDir : "asc"}
                    onClick={() => handleSort("username")}
                  >
                    Username
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortKey === "email" ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === "email"}
                    direction={sortKey === "email" ? sortDir : "asc"}
                    onClick={() => handleSort("email")}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>Icon</TableCell>
                <TableCell>Origin ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow
                  data-testid={"userTableRow" + row.id}
                  hover
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}
                  onClick={() => navigate(`/user/${row.id}`)}
                >
                  {props.showActions && (
                    <TableCell sx={{ width: 48, p: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        onEdit={() => setEditRow(row)}
                        onDelete={() => setDeleteRow(row)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{row.discriminator}</TableCell>
                  <TableCell>{row.discordId}</TableCell>
                  <TableCell>{row.username}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <img
                      src={row.icon || transparentLogo}
                      alt={`${row.username} profile icon`}
                      style={{ width: "50px" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = transparentLogo;
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.originId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editRow && (
        <EditUserDialog
          open
          setOpen={(open) => !open && setEditRow(null)}
          user={editRow}
        />
      )}
      {deleteRow && (
        <DeleteUserDialog
          open
          setOpen={(open) => !open && setDeleteRow(null)}
          user={deleteRow}
        />
      )}
    </>
  );
};

export default UserTable;
